import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { useIntl } from "react-intl";
import { useSetAtom } from "jotai";
import { Intent, Skill } from "@/model";
import skillRepository from "@/repository/skillRepository";
import { useDeleteIntents } from "@/query/intent";
import { intentTableScrollToIntentIdAtom } from "@/atom/intentTable";
import { useAgentId, useDialog, useModal, useRouteHandler, useSelection } from "@/hook";
import { IndeterminateCheckbox } from "@/component/input";
import { errorToast, successToast } from "@/component/Toast";
import SelectionActionBar from "@/component/SelectionActionBar";
import SortButton from "@/component/SortBurtton";
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";

import IntentTableVirtualizer from "../IntentTableVirtualizer";
import { INTENT_TRANSFER_MODAL_ID } from "./IntentsTransferModal";


export const INTENT_UPLOAD_MODAL_ID = "intent-upload-modal";

interface IntentTableProps {
    intents?: Intent[];
    skill?: Skill;
    searchType: string;
    searchKeyword?: string;
}

const IntentTable: React.FC<IntentTableProps> = (props) => {
    const { intents, skill, searchType, searchKeyword } = props;

    const intl = useIntl();
    const modal = useModal();
    const agentId = useAgentId();
    const dialog = useDialog();
    const location = useLocation();
    const { moveToIntent } = useRouteHandler();

    const [order, setOrder] = useState<"asc" | "desc">("asc");
    const [allOpen, setAllOpen] = useState<boolean>(false);
    const [openIntentIds, setOpenIntentIds] = useState<string[]>([]);

    const setScrollToIntentId = useSetAtom(intentTableScrollToIntentIdAtom);
    const { mutateAsync: deleteIntents } = useDeleteIntents(agentId, skill?.id);

    const showIntentIds = useMemo(() => {
        let filteredIntents = intents ?? [];
        if (searchKeyword) {
            const keyword = searchKeyword.toLowerCase();
            switch (searchType) {
                case "intent":
                    filteredIntents = filteredIntents.filter(intent => intent.name.toLowerCase().includes(keyword));
                    break;
                case "intentSentence":
                    filteredIntents =
                        filteredIntents.filter(intent =>
                            intent.sentences.some(sentence =>
                                sentence.origin_sentence.toLowerCase().includes(keyword)
                            )
                        );
                    break;
                default:
                    filteredIntents = [];
            }
        }
        return filteredIntents.map(intent => intent.id) ?? [];
    }, [intents, searchKeyword, searchType]);

    const { selectionCount, pageSelectionStatus, selectedIds, markOne, markPage, markAll, clearSelection } = useSelection({
        targetIds: showIntentIds,
        total: showIntentIds.length
    });

    const onClickDelete = async () => {
        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i03029" }),
            content: intl.formatMessage({ id: "i03030" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: async () => {
                await deleteIntents(selectedIds)
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t005" }));
                        clearSelection();
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t006" }));
                    });
            }
        });
    };

    const onClickIntentOpen = (id: string, open: boolean) => {
        if (open) {
            setOpenIntentIds(prev => prev.includes(id) ? prev : [...prev, id]);
        } else {
            setOpenIntentIds(prev => prev.filter(prevId => prevId !== id));
        }
    };

    const sortedIntents = useMemo(() => {
        if (!intents)
            return [];

        const isAsc = order === "asc";
        return [...intents].sort((a, b) => {
            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();
            if (aName === bName) return 0;
            if (isAsc) {
                return aName > bName ? 1 : -1;
            } else {
                return aName < bName ? 1 : -1;
            }
        });
    }, [intents, order]);

    useEffect(() => {
        setOpenIntentIds(prev => prev.filter(id => showIntentIds.includes(id)));
    }, [showIntentIds]);

    useEffect(() => {
        const intentId = location.state?.intentId;
        if (!intentId) {
            return;
        }
        setOpenIntentIds(prev => prev.includes(intentId) ? prev : [...prev, intentId]);
        setScrollToIntentId(intentId);
        moveToIntent(agentId, { replace: true, state: null });
        // eslint-disable-next-line
    }, [location.state?.intentId]);

    useEffect(() => {
        setAllOpen(!!searchKeyword && searchType === "intentSentence");
        // eslint-disable-next-line
    }, [setAllOpen, skill, searchKeyword, searchType]);

    useEffect(() => {
        if (allOpen) {
            setOpenIntentIds(sortedIntents.map(intent => intent.id));
        } else {
            setOpenIntentIds([]);
        }
        // eslint-disable-next-line
    }, [sortedIntents, allOpen, setOpenIntentIds]);

    useEffect(() => {
        clearSelection();
        // eslint-disable-next-line
    }, [skill]);

    if (!skill || !intents) {
        return null;
    }
    return (
        <div role="table" className="h-fit max-h-full overflow-hidden flex flex-col border border-black-200 bg-white rounded-[6px]">
            <div role="rowgroup"
                 className="w-full min-h-table-row flex items-center border-b border-black-200 text-black-500 text-[14px] font-[600] leading-[1.5]"
            >
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <IndeterminateCheckbox status={pageSelectionStatus}
                                           onChange={checked => markPage(checked)}
                    />
                </div>
                <div role="columnheader">
                    <SortButton order={order}
                                onClick={() => setOrder(prev => prev === "asc" ? "desc" : "asc")}
                    >
                        {intl.formatMessage({ id: "i03055" })}
                    </SortButton>
                </div>

                <div role="columnheader" className="ml-auto">
                    <button className="m-2 flex justify-between items-center"
                            onClick={() => setAllOpen(prev => !prev)}
                    >
                        <div className="text-black-500 text-[14px] font-[400] leading-[1.5]">
                            {intl.formatMessage({ id: allOpen ? "i03062" : "i03061" })}
                        </div>
                        <ChevronArrowIcon direction={allOpen ? "up" : "down"}
                                          className="size-4 ml-2"  
                        />
                    </button>
                </div>
            </div>
            {showIntentIds.length > 0
                ? (
                    <IntentTableVirtualizer intents={sortedIntents}
                                            selectedIntentIds={selectedIds}
                                            showIntentIds={showIntentIds}
                                            openIntentIds={openIntentIds}
                                            onCheck={markOne}
                                            onClickOpen={onClickIntentOpen}
                    />
                ) : (
                    <div className="empty-table">
                        {intl.formatMessage({ id: searchKeyword ? "b0014" : "b0018" })}
                    </div>
                )
            }

            <SelectionActionBar selectionCount={selectionCount}
                                selectAll={() => markAll(true)}
                                deSelect={() => markAll(false)}
                                remove={onClickDelete}
                                move={() => modal.open(INTENT_TRANSFER_MODAL_ID, selectedIds)}
                                download={() => skillRepository.export(agentId, selectedIds)}
            />
        </div>
    );
};

export default IntentTable;
